<template>
  <main class="wallet-nft-detail-page">
    <AppNavBar :title="t('wallet.nftDetail')" />

    <section class="nft-card">
      <div class="nft-card__head">
        <div class="nft-card__icon">
          <app-image v-if="nftInfo.img" :src="nftInfo.img" alt="" />
          <div v-else class="nft-card__icon-fallback">
            {{ nftName.slice(0, 1).toUpperCase() }}
          </div>
        </div>

        <div class="nft-card__meta">
          <div data-strong>{{ nftName }}</div>
          <div class="nft-card__address">
            <span>{{ contractAddress || "--" }}</span>
            <button
              type="button"
              class="nft-card__copy"
              @click="copyText(contractAddress)"
            >
              <app-image :src="copyIcon" alt="" />
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="asset-section">
      <div class="asset-section__title">{{ t("wallet.nftAssets") }}</div>

      <van-pull-refresh v-model="refreshing" @refresh="refreshAssets">
        <div v-if="!assetList.length && !loading" class="empty-state">
          <app-image :src="emptyImage" alt="" />
          <span>{{ t("home.noData") }}</span>
        </div>

        <button
          v-for="item in assetList"
          :key="assetKey(item)"
          type="button"
          class="asset-row"
          @click="openAsset(item)"
        >
          <div class="asset-row__cover">
            <app-image v-if="item.img" :src="item.img" alt="" />
            <div v-else class="asset-row__cover-fallback">NFT</div>
          </div>

          <div class="asset-row__meta">
            <div data-strong>#{{ item.tokenId || "--" }}</div>
          </div>

          <van-icon name="arrow" class="asset-row__arrow" />
        </button>
      </van-pull-refresh>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { nftList } from "@/api/wallet";
import copyIcon from "@/assets/images/copy_icon_gray.png";
import emptyImage from "@/assets/images/emptydata_gray.png";
import { useProfileStore } from "@/stores/profile";
import { useWalletNftStore } from "@/stores/wallet-nft";
import { copyToClipboard } from "@/utils/clipboard";
import { showPassedToast } from "@/utils/toast";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletNftStore = useWalletNftStore();

const refreshing = ref(false);
const loading = ref(false);
const assetList = ref([]);

const nftInfo = computed(() => walletNftStore.currentNft || null);
const nftName = computed(() =>
  String(nftInfo.value?.name || nftInfo.value?.symbol || "NFT"),
);
const contractAddress = computed(() =>
  String(nftInfo.value?.contractAddress || "").trim(),
);
const protocolType = computed(() =>
  String(nftInfo.value?.protocolType || "").trim(),
);

function normalizeAsset(item = {}) {
  return {
    ...item,
    img: item?.img || item?.image || "",
    tokenId: item?.tokenId || item?.id || "",
    contractAddress: item?.contractAddress || contractAddress.value,
    protocolType: item?.protocolType || protocolType.value,
    name: item?.name || nftName.value,
  };
}

function assetKey(item = {}) {
  return `${item.contractAddress || ""}:${item.tokenId || item.id || ""}`;
}

async function copyText(text) {
  if (!text) {
    return;
  }
  const copied = await copyToClipboard(String(text));
  if (copied) {
    showPassedToast(t("wallet.copySuccess"));
    return;
  }
  showFailToast(t("wallet.copyFailed"));
}

async function loadAssets() {
  if (!nftInfo.value?.contractAddress) {
    router.replace("/home/index");
    return;
  }

  loading.value = true;
  try {
    const result = await nftList(
      {
        address: profileStore.platformAddress || "",
        chainNum: profileStore.currentNetwork?.chainNum || 56,
        contractAddress: contractAddress.value,
        protocolType: protocolType.value,
      },
      { meta: { showLoading: false } },
    );

    const rows = Array.isArray(result?.list)
      ? result.list
      : Array.isArray(result)
        ? result
        : [];
    assetList.value = rows.map(normalizeAsset);
  } finally {
    loading.value = false;
  }
}

async function refreshAssets() {
  await loadAssets();
  refreshing.value = false;
}

function openAsset(item) {
  walletNftStore.setCurrentNftAsset(normalizeAsset(item));
  router.push("/wallet/nft-view");
}

onMounted(loadAssets);
</script>

<style scoped lang="scss">
.wallet-nft-detail-page {
  min-height: var(--app-viewport-height);
  background: #f5f5f5;
  padding-bottom: env(safe-area-inset-bottom);
}

.nft-card {
  margin-top: 10px;
  padding: 15px;
  background: #fff;
}

.nft-card__head {
  display: flex;
  align-items: center;
}

.nft-card__icon {
  width: 60px;
  height: 60px;
  margin-right: 15px;
  border-radius: 30px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;

  .app-image,
  .nft-card__icon-fallback {
    width: 100%;
    height: 100%;
  }

  .app-image {
    object-fit: cover;
    border-radius: 8px;
  }
}

.nft-card__icon-fallback {
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  background: var(--wp-color-brand-1);
}

.nft-card__meta {
  min-width: 0;
  flex: 1;
}

.nft-card__meta [data-strong] {
  display: block;
  color: #111;
  font-size: 18px;
  line-height: 25px;
  font-weight: 700;
}

.nft-card__address {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.nft-card__address span {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #999;
  font-size: 12px;
  line-height: 17px;
}

.nft-card__copy {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  background: transparent;
  flex-shrink: 0;

  .app-image {
    width: 16px;
    height: 16px;
    display: block;
  }
}

.asset-section {
  margin-top: 10px;
  padding: 5px 15px 0;
  background: #fff;
  min-height: calc(var(--app-viewport-height) - 158px);
}

.asset-section__title {
  padding: 15px 0;
  color: #111;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}

.asset-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  border: 0;
  border-bottom: 1px solid #f1f1f1;
  background: transparent;
  text-align: left;
}

.asset-row:last-child {
  border-bottom: 0;
}

.asset-row__cover {
  width: 40px;
  height: 40px;
  margin-right: 10px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;

  .app-image,
  .asset-row__cover-fallback {
    width: 100%;
    height: 100%;
  }

  .app-image {
    object-fit: cover;
  }
}

.asset-row__cover-fallback {
  display: grid;
  place-items: center;
  color: #999;
  font-size: 10px;
  font-weight: 500;
}

.asset-row__meta {
  min-width: 0;
  flex: 1;
  text-align: left;
}

.asset-row__meta [data-strong] {
  color: #999;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.asset-row__arrow {
  flex-shrink: 0;
  color: #999999;
  font-size: 18px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 0;
  color: #999;
  font-size: 14px;

  .app-image {
    width: 88px;
    height: 88px;
    object-fit: contain;
  }
}
</style>
